import { COLORS } from '@btime/constants'
import { concatIf } from '@btime/helpers'
import { gql } from '@apollo/client'
import { head, propEq } from 'ramda'
import { useSessionPermission } from '@btime/react-session'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Grid } from '../../packages/kiti/components/structure/Grid'
import { generalClient } from '../../utils/apollo/api'
import { toKebabCase } from '../../utils/helpers/string'
import { useNavigation } from '../../utils/contexts/NavigationContext'
import { useOpenEditScreenModal } from '../../utils/routes/modals/modalsActions'
import { useQuery } from '../../utils/apollo/_helpers/useQuery'
import { useRemoveScreen } from './_request/removeScreenRequest'
import DropdownLink from '../../components/navigation/DropdownLink'
import Icon from '../../packages/kiti/components/shapes/Icon'
import IconButton from '../../components/actions/IconButton'
import Link from '../../packages/kiti/components/actions/Link'
import Loading from '../../packages/kiti/components/helpers/Loading'

const QUERY = gql`
  query Screens($filter: ScreenFilter) {
    screens(filter: $filter, limit: 100, sort: { field: NAME, type: ASC }) {
      id
      name
    }
  }
`

const ScreenDropdown = ({ selected, screen }) => {
  const { t } = useTranslation('common')
  const navigate = useNavigation()
  const canCreateScreen = useSessionPermission('setupScreen')
  const openEditScreen = useOpenEditScreenModal()
  const [removeScreen, { loading }] = useRemoveScreen()
  const urlPrefix = toKebabCase(screen)

  const { data } = useQuery(QUERY, {
    fetchPolicy: 'cache-first',
    client: generalClient,
    variables: { filter: { screens: screen } }
  })

  const handleRemoveFilter = screenId => e => {
    e.stopPropagation()
    removeScreen(screenId)
    if (`kanban/${screenId}` === selected) {
      navigate(`/${urlPrefix}/list`)
    }
  }

  if (loading) return <Loading />

  const options = [
    {
      label: t('views.list'),
      value: 'list',
      iconLeft: 'BsListTask',
      onClick: () => navigate(`/${urlPrefix}/list`)
    },

    ...concatIf(screen === 'service_orders', [
      {
        label: t('views.calendar'),
        value: 'calendar',
        iconLeft: 'RiCalendarCheckLine',
        onClick: () => navigate(`/${urlPrefix}/calendar`)
      }
    ]),

    ...(data?.screens || []).map(screen => ({
      label: screen.name,
      value: `kanban/${screen.id}`,
      iconLeft: 'BsKanban',
      onClick: () => navigate(`/${urlPrefix}/kanban/${screen.id}`),
      rightContent: canCreateScreen && (
        <Grid gap={0.6}>
          <Link onClick={() => openEditScreen({ id: screen.id })}>
            <Icon name="BsPencil" size={0.8} color="dark3" />
          </Link>
          <Link onClick={() => openEditScreen({ id: screen.id, isCloning: true })}>
            <Icon name="BsLayers" size={0.8} color="dark3" />
          </Link>
          <Link onClick={handleRemoveFilter(screen.id)}>
            <Icon name="BsTrash" size={0.8} color="red" />
          </Link>
        </Grid>
      )
    })),

    ...concatIf(canCreateScreen, [
      {
        label: t('newKanban'),
        value: 'new',
        iconLeft: 'BsBookmarkPlus',
        onClick: () => openEditScreen({ initialState: { screen } }),
        color: COLORS.purple
      }
    ])
  ]

  const activeOption = options.find(propEq('value', selected)) || head(options)

  return (
    <DropdownLink
      actionContent={<IconButton iconLeft={activeOption?.iconLeft} label={activeOption?.label} transparent noWrap />}
      options={options}
    />
  )
}

export default ScreenDropdown
